import { useMemo, useState } from "react";
import SlipTicket from "../components/SlipTicket";
import SlipSyncSettings from "../features/slipsync/SlipSyncSettings";
import ResultsPage from "./ResultsPage";
import { dkConfig } from "../lib/slipsync/books/dk";
import { JT } from "../components/ui/jumbotronTokens";
import { SyncSkeleton, SyncErrorCard } from "../components/ui/SyncState";
import RemoveBetSheet from "../components/ui/RemoveBetSheet";
import { useRemoveBetTarget } from "../hooks/useRemoveBetTarget";
import { betDisplayStatus } from "../lib/betting";

// #1353 PR 3b (r5b.4 I1): client grading trusts the server-written DNP flag (as SlipTicket).
const TRUST_DNP = { trustDnpFlag: true };

// syncLoading / syncError come from useCloudSync (App.js); guests never set either, so the
// local-bets path is untouched (UI critique bug-fix batch item C).
export default function MySlipsPage({bets,games,liveStats,oddsData,onAdd,haptic,user,onSignIn,onSyncComplete,onOpenPlayerStats,refetchBetaStatus,syncLoading=false,syncError=false,onRetrySync,removeBet}) {
  // #896: memoize on [bets] so `active` is a stable reference. Previously
  // `bets.filter(...)` produced a new array every render, so the atRisk memo
  // (keyed on [active]) never hit and filter()+reduce() re-ran each render.
  const active = useMemo(()=>bets.filter(b=>!b.settledAt),[bets]);
  // #1655 B3b (P2-3, D3): a NOT GRADED slip stays on ACTIVE but nothing is at risk on it any
  // more -- its game is over and only grading is missing. Same status call (and multi
  // definition) as the SlipTicket that renders it.
  const atRisk = useMemo(()=>active
    .filter(b=>betDisplayStatus(b,games,{ isMulti: b.slipType==="parlay"||b.slipType==="sgp", ...TRUST_DNP })!=="not_graded")
    .reduce((s,b)=>s+(Number(b.stake)||0),0),[active,games]);
  const fmtRisk = Number.isInteger(atRisk) ? atRisk : atRisk.toFixed(2);
  // BETS tab in-page view: 'active' = live/pending slips, 'history' = settled ResultsPage.
  const [viewMode, setViewMode] = useState("active");
  // #1214 PR B (spec §16.3, P1-5): ONE Remove sheet for the active list and History. It holds
  // { betId, summary } captured when it opens, so it outlives the card that opened it (a realtime
  // settlement can move the bet to History mid-request). Guests never get the ⋯ (§16.2).
  const { target: removeTarget, onRequestRemove, closeRemove } = useRemoveBetTarget({ user, removeBet });
  const toggle = (mode, label, badge) => {
    const on = viewMode === mode;
    return (
      <button onClick={()=>setViewMode(mode)} style={{
        background: on ? JT.brand.cta : JT.bg.well,
        color: on ? JT.text.primary : JT.text.tertiary,
        border: on ? "1px solid transparent" : `1px solid ${JT.border.default}`,
        borderRadius: JT.radius.pill, fontSize:11, fontWeight:800, padding:"0 16px", minHeight:44,
        cursor:"pointer", fontFamily:"inherit",
      }}>
        {label}{badge!=null && <span style={{ marginLeft:6, opacity:.85 }}>{badge}</span>}
      </button>
    );
  };
  return (
    <>
      <div style={{ padding:"20px 16px", animation:"fadeUp .3s ease" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
          <div style={{ ...JT.display900, fontSize:42, color:JT.text.primary, lineHeight:1 }}>MY BETS</div>
          <div aria-hidden="true" style={{ ...JT.display900, fontSize:11, letterSpacing:"4px", color:JT.text.dim }}>SLIPTRACK</div>
        </div>
        {viewMode==="active" && !syncLoading && !syncError && (
          <div style={{ ...JT.type.label, letterSpacing:"1.5px", color:JT.text.tertiary, marginBottom:14 }}>
            {active.length} SLIP{active.length!==1?"S":""} · ${fmtRisk} AT RISK
          </div>
        )}
        <div style={{ display:"flex", gap:8, marginBottom:16 }}>
          {toggle("active","ACTIVE",(syncLoading||syncError)?null:active.length)}
          {toggle("history","HISTORY",null)}
        </div>
        {viewMode==="active" && (
          <>
            <SlipSyncSettings user={user} onSignIn={onSignIn} onSyncComplete={onSyncComplete}/>
            <SlipSyncSettings user={user} onSignIn={onSignIn} onSyncComplete={onSyncComplete} book={dkConfig}/>
            {syncLoading ? (
              <SyncSkeleton count={3} height={120}/>
            ) : syncError ? (
              <SyncErrorCard onRetry={onRetrySync}/>
            ) : active.length===0 ? (
              <div style={{ textAlign:"center", padding:"60px 24px", animation:"fadeUp .4s ease" }}>
                <div style={{ fontSize:64, marginBottom:16 }}>🎟️</div>
                <div style={{ ...JT.display900, fontSize:20, color:JT.text.primary, marginBottom:8 }}>No active slips</div>
                <div style={{ fontSize:13, color:JT.text.secondary, marginBottom:28, lineHeight:1.6 }}>Import a screenshot of your bet slip to start tracking your bets in real time.</div>
                <button onClick={onAdd} style={{ background:JT.brand.cta, border:"none", borderRadius:JT.radius.pill, padding:"14px 32px", color:JT.text.primary, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit", boxShadow:JT.brand.ctaShadow }}>
                  + Add Your First Slip
                </button>
              </div>
            ) : (
              active.map(bet=><SlipTicket key={bet.id} bet={bet} games={games} liveStats={liveStats} oddsData={oddsData} haptic={haptic} onOpenPlayerStats={onOpenPlayerStats} onRequestRemove={onRequestRemove}/>)
            )}
            {/* No per-page bottom CTA: the IMPORT tab (TabBar) is the global add-slip
                affordance below every page. The empty-state button above is the only
                in-page add affordance; the active list uses the IMPORT tab (Codex P1, PR #886). */}
          </>
        )}
      </div>
      {viewMode==="history" && (
        <ResultsPage bets={bets} games={games} refetchBetaStatus={refetchBetaStatus} syncLoading={syncLoading} syncError={syncError} onRetrySync={onRetrySync} onRequestRemove={onRequestRemove}/>
      )}
      <RemoveBetSheet key={user?.id ?? "guest"} target={removeTarget} onClose={closeRemove} onRemoved={removeBet} haptic={haptic}/>
    </>
  );
}
